import { useEffect, useMemo, useState } from 'react'

import { getPageLayoutOption, pageMaxPanels } from '../../lib/comicPageLayout'
import type { ComicPage, ComicPanel } from '../../lib/comicTypes'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { chromeToggleChipClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { HorizontalChipStrip } from '../HorizontalChipStrip'
import { managementListActionClass } from '../ManagementListModal'
import { ThemeModal } from '../modal'

type Props = {
  open: boolean
  angular: boolean
  sourcePage: ComicPage | null
  panels: ComicPanel[]
  pages: ComicPage[]
  onClose: () => void
  onMove: (panelIds: string[], targetPageId: string) => void
}

function panelScriptTooltip(script: string, maxLen = 10): string {
  const raw = script.trim() || '（未填写）'
  if (raw.length <= maxLen) return raw
  return `${raw.slice(0, maxLen)}…`
}

function roomOnTarget(page: ComicPage, movingIds: readonly string[]): number {
  const max = pageMaxPanels(page.layout)
  const kept = page.panelIds.filter((id) => !movingIds.includes(id)).length
  const incoming = movingIds.filter((id) => !page.panelIds.includes(id)).length
  return max - kept - incoming
}

export function ComicPanelBatchPageMapModal({
  open,
  angular,
  sourcePage,
  panels,
  pages,
  onClose,
  onMove,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [targetPageId, setTargetPageId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSelectedIds(panels.map((p) => p.id))
    const firstTarget = pages.find((p) => p.id !== sourcePage?.id)?.id ?? null
    setTargetPageId(firstTarget)
  }, [open, panels, pages, sourcePage?.id])

  const targetPage = targetPageId ? pages.find((p) => p.id === targetPageId) ?? null : null
  const room = targetPage ? roomOnTarget(targetPage, selectedIds) : 0
  const canSubmit =
    selectedIds.length > 0 &&
    targetPageId != null &&
    targetPageId !== sourcePage?.id &&
    room >= 0

  const targetEntries = useMemo(
    () =>
      pages
        .filter((p) => p.id !== sourcePage?.id)
        .map((page) => {
          const r = roomOnTarget(page, selectedIds)
          const active = page.id === targetPageId
          return {
            id: page.id,
            primary: page.title,
            badge: active ? '目标页' : undefined,
            detail: `${getPageLayoutOption(page.layout).label} · ${r < 0 ? '容量不足' : `可再收 ${r} 格`}`,
            actions: (
              <ChromeToolbarButton
                type="button"
                className={managementListActionClass(angular)}
                disabled={r < 0}
                onClick={() => setTargetPageId(page.id)}
              >
                {active ? '已选' : r < 0 ? '已满' : '选为目标'}
              </ChromeToolbarButton>
            ),
          }
        }),
    [angular, pages, selectedIds, sourcePage?.id, targetPageId],
  )

  const togglePanel = (id: string) => {
    setSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    )
  }

  const allSelected = panels.length > 0 && selectedIds.length === panels.length

  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title="批量迁移"
      angular={angular}
      size="md"
      bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <ChromeToolbarButton type="button" onClick={onClose}>
            取消
          </ChromeToolbarButton>
          <ChromePrimaryButton
            density="compact"
            type="button"
            disabled={!canSubmit || room < 0}
            onClick={() => {
              if (!targetPageId || !canSubmit) return
              onMove(selectedIds, targetPageId)
              onClose()
            }}
          >
            迁移 {selectedIds.length} 格
          </ChromePrimaryButton>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-ink-soft">
              {sourcePage ? `${sourcePage.title} · 选择分镜` : '选择分镜'}
            </p>
            {panels.length > 0 ? (
              <ChromeToolbarButton
                type="button"
                className="h-8 px-2 text-[11px]"
                onClick={() =>
                  setSelectedIds(allSelected ? [] : panels.map((p) => p.id))
                }
              >
                {allSelected ? '全不选' : '全选'}
              </ChromeToolbarButton>
            ) : null}
          </div>
          {panels.length === 0 ? (
            <p className="text-sm text-ink-soft">当前页没有分镜</p>
          ) : (
            <HorizontalChipStrip className="pb-1" trackClassName="gap-2" role="list">
              {panels.map((panel, idx) => {
                const on = selectedIds.includes(panel.id)
                const hint = panelScriptTooltip(panel.script)
                return (
                  <ChromeTooltip
                    key={panel.id}
                    content={hint}
                    angular={angular}
                    side="top"
                    align="center"
                  >
                    <button
                      type="button"
                      role="listitem"
                      className={[
                        chromeToggleChipClass(angular, on),
                        'shrink-0 px-3 py-2 text-sm font-semibold tabular-nums',
                      ].join(' ')}
                      aria-pressed={on}
                      aria-label={`格 ${idx + 1}：${hint}`}
                      onClick={() => togglePanel(panel.id)}
                    >
                      格 {idx + 1}
                    </button>
                  </ChromeTooltip>
                )
              })}
            </HorizontalChipStrip>
          )}
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold text-ink-soft">目标分页</p>
          {targetEntries.length === 0 ? (
            <p className="text-sm text-ink-soft">没有其他分页</p>
          ) : (
            <ul className="space-y-2">
              {targetEntries.map((e) => (
                <li
                  key={e.id}
                  className={[
                    'flex flex-col gap-2 border border-line bg-panel p-3 sm:flex-row sm:items-center sm:justify-between',
                    angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]',
                    e.id === targetPageId ? 'ring-1 ring-inset ring-accent/40' : '',
                  ].join(' ')}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink">{e.primary}</p>
                    <p className="mt-0.5 text-xs text-ink-soft">{e.detail}</p>
                  </div>
                  {e.actions}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ThemeModal>
  )
}
